import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import { supabaseConfigured } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { isNative } from "../lib/runtime";
import { Button } from "../components/ui/Button";
import { Field, Input } from "../components/ui/Input";

// Sign-in only. Account creation now lives behind a paid Stripe
// checkout (see pages/Landing.tsx + supabase/functions/stripe-webhook).
// The Login page never offers a signup path — Apple's App Store
// guidelines explicitly disallow alternate signup mechanisms in a
// free app, and our threat model is the same on the web (every
// account must come through a paid checkout or a supervisor's
// invite).
//
// Forgot-password path: the "Forgot password?" link flips the form
// into a single-field email prompt. On submit we call Supabase's
// resetPasswordForEmail — the user gets a magic link that App.tsx's
// existing recovery deep-link handler catches, opens a session,
// and routes into the AcceptInvite page for a new password.
export function Login() {
  const setUser = useAuth((s) => s.setUser);
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"signin" | "forgot" | "sent">("signin");
  const [resetEmail, setResetEmail] = useState("");

  const signIn = useMutation({
    mutationFn: () => api.login({ emailOrUsername, password }),
    onSuccess: (u) => { setUser(u); },
    onError: (e: { message?: string }) => setError(e?.message ?? "Login failed."),
  });

  const resetMut = useMutation({
    mutationFn: () => api.sendPasswordReset(resetEmail),
    onSuccess: () => { setMode("sent"); setError(null); },
    onError: (e: { message?: string }) => setError(e?.message ?? "Couldn't send reset email."),
  });

  function onSignInSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    signIn.mutate();
  }
  function onResetSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    resetMut.mutate();
  }

  return (
    <div className="h-full w-full flex items-center justify-center bg-slate-100 px-4">
      {mode === "signin" && (
        <form
          onSubmit={onSignInSubmit}
          className="w-full max-w-sm bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4"
        >
          <div>
            <h1 className="text-lg font-semibold text-slate-900">ShiftManager</h1>
            <p className="text-sm text-slate-500 mt-0.5">Sign in to continue.</p>
          </div>
          <Field label="Email or username">
            <Input
              autoFocus
              autoComplete="username"
              value={emailOrUsername}
              onChange={(e) => setEmailOrUsername(e.target.value)}
              type="text"
            />
          </Field>
          <Field label="Password">
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="pt-1 flex">
            <Button
              type="submit"
              disabled={signIn.isPending}
              className="w-full"
            >
              {signIn.isPending ? "Signing in…" : "Sign in"}
            </Button>
          </div>
          {supabaseConfigured && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  // Pre-populate the reset field if the user typed
                  // something that looks like an email into the
                  // sign-in box — saves a keystroke.
                  if (emailOrUsername.includes("@")) setResetEmail(emailOrUsername);
                  setError(null);
                  setMode("forgot");
                }}
                className="text-sm text-sky-700 hover:underline font-medium"
              >
                Forgot password?
              </button>
            </div>
          )}
          {supabaseConfigured && (
            <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 leading-relaxed">
              <strong className="text-slate-800">Got an invite email?</strong>{" "}
              Click the link inside to set your password and finish
              setting up your account — don't sign in here yet.
            </div>
          )}
          {/* Web only: link to the paid "Start your agency" page.
            * Hidden on the iOS Capacitor build because Apple won't
            * approve a free app that links out to off-platform paid
            * signup; iOS users come in through invites. */}
          {supabaseConfigured && !isNative && (
            <div className="text-center text-sm text-slate-600 pt-1 border-t border-slate-100">
              <span>Don't have an agency yet?</span>{" "}
              <a
                href="#/start"
                className="text-sky-700 hover:underline font-medium"
              >
                Start your agency →
              </a>
            </div>
          )}
        </form>
      )}

      {mode === "forgot" && (
        <form
          onSubmit={onResetSubmit}
          className="w-full max-w-sm bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4"
        >
          <div>
            <h1 className="text-lg font-semibold text-slate-900">
              Reset your password
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Enter the email address on your account and we'll send
              you a link to set a new password.
            </p>
          </div>
          <Field label="Email address">
            <Input
              autoFocus
              type="email"
              autoComplete="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="you@youragency.gov"
            />
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={resetMut.isPending}
              className="flex-1"
            >
              {resetMut.isPending ? "Sending…" : "Send reset link"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => { setMode("signin"); setError(null); }}
              disabled={resetMut.isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {mode === "sent" && (
        <div className="w-full max-w-sm bg-white border border-slate-200 rounded-lg shadow-sm p-6 space-y-4 text-center">
          <div className="mx-auto w-10 h-10 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-700 text-lg">
            ✓
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Check your inbox</h1>
            <p className="text-sm text-slate-500 mt-2">
              If an account exists for <strong>{resetEmail}</strong>,
              we've sent a password-reset link. Click the link in the
              email to set a new password.
            </p>
            <p className="text-xs text-slate-500 mt-3">
              Didn't get it? Check your spam folder, then try again
              from the sign-in page.
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              setMode("signin");
              setResetEmail("");
              setError(null);
            }}
            className="w-full"
          >
            Back to sign in
          </Button>
        </div>
      )}
    </div>
  );
}
